import React, { createContext, useContext, useMemo } from 'react';
import { Publishable } from '~/types/models/publishable';
import { usePublishable } from '~/App/Api/Publishable/api';
import { isAxiosError } from 'axios';

import Error404 from '~/Errors/Pages/error404';
import { useZiggy } from '~/react-hooks/use-ziggy';

export interface PublishPageContextProps {
  publish: Publishable;
  totalAssets?: number;
}

const PublishPageContext = createContext<
  PublishPageContextProps | undefined | null
>(undefined);

interface PublishPageContextProviderProps {
  uuid: string;
  children: React.ReactNode;
}

const PublishPageContextProvider = ({
  uuid,
  children,
}: PublishPageContextProviderProps) => {
  const { data, isLoading, isError, error } = usePublishable(
    { uuid },
    {
      refetchOnWindowFocus: false,
    }
  );

  const { route } = useZiggy();

  const contextValue: PublishPageContextProps = useMemo(
    () => ({
      publish: data?.[0] as Publishable,
      totalAssets: data?.[1]?.total_assets,
    }),
    [data]
  );

  if (isLoading || !data) {
    return null;
  }

  if (isError && isAxiosError(error)) {
    if (error.response?.status === 404) {
      return (
        <Error404
          noFullPage
          ctaText='Return to list'
          ctaLink={route('publish::index')}
        />
      );
    }

    throw error;
  }

  return (
    <PublishPageContext.Provider value={contextValue}>
      {children}
    </PublishPageContext.Provider>
  );
};

const usePublishPageContext = (): PublishPageContextProps => {
  const context = useContext(PublishPageContext);

  return context as PublishPageContextProps;
};

export {
  PublishPageContext,
  PublishPageContextProvider,
  usePublishPageContext,
};
